import { useIntl, defineMessages } from 'react-intl';
// Removed react-instantsearch usage
import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useDebugRenderIf, useDebugEffectIf, useDebugStateIf, setDebugRenderEnabled, isDebugRenderEnabled } from '../../utils/debug/debug-render';
import { PerformanceProfiler, useRenderLogger } from '../../utils/shared/performance-profiler';
// import { CanonicalTag } from '../../seo_components/CanonicalTag';
import { KeyboardShortcutsTray } from '../../components/KeyboardShortcutsTray';
import { useData } from 'vike-react/useData';
import { usePageContext } from 'vike-react/usePageContext';
import { useSearchResults, useSearchSort, useSearchQuery, useSearchFilters, useSearchPage, useSearchContext, useSearchContextOptional } from '../../contexts/SearchContext';
import { useLocationTrends } from '../../hooks/useLocationTrends';
import type { DisplayJob } from '../../types';
import { FeedbackModal } from '../../components/FeedbackModal';
import { SearchHeader } from './components/SearchHeader';
import { SearchResults } from './components/SearchResults';
import { SearchSidebar } from './components/SearchSidebar';
import { SearchPagination } from './components/SearchPagination';
import { SrpAlertCta } from '../../components/job-alerts/SrpAlertCta';
import { useViewMode } from './hooks/useViewMode';
import { computeSearchPageMeta, getLocationFromFiltersAndRoute } from './utils/seoUtils';
import { getCategoryFromFiltersAndRoute } from '../../utils/category/get-category-from-filters-and-route';

import Footer from '../../components/Footer';
import { TopEmployersStripWithClientFetch } from '../../components/TopEmployersStripWithClientFetch';
import { SrpActivitySankeyStripLive } from '../../components/srp/SrpActivitySankeyStrip';

const KEYWORDS = {
  fi: 'avoimet työpaikat, tuoreet työpaikat, työpaikat Suomessa, työpaikkailmoitukset, työhaku, rekrytointi, etätyö, kokoaikatyö, osa-aikatyö, IT-työpaikat, sote-työpaikat, rakennusalan työpaikat, asiakaspalvelutyö, markkinointityö, Suomen suurin työpaikkakokoelma, etätyöt Euroopasta, etätyöt maailmasta, jobs in Finland',
  en: 'open jobs, fresh jobs, jobs in Finland, job postings, job search, recruitment, remote work, full-time jobs, part-time jobs, IT jobs, healthcare jobs, construction jobs, customer service jobs, marketing jobs, largest Finnish job collection, remote jobs Europe, remote jobs for Finnish residents, jobs in Finland',
  sv: 'lediga jobb, nya jobb, jobb i Finland, platsannonser, jobbsökning, rekrytering, distansarbete, heltidsjobb, deltidsjobb, IT-jobb, vårdjobb, byggjobb, kundtjänstjobb, marknadsföringsjobb, Finlands största jobbsamling, distansjobb från Europa, distansjobb för finska invånare, jobb i Finland',
};

type SupportedLocale = 'en' | 'fi' | 'sv';

const SUPPORTED_LOCALES: SupportedLocale[] = ['en', 'fi', 'sv'];
const JOBS_SEGMENT_BY_LOCALE: Record<SupportedLocale, string> = {
  en: 'jobs',
  fi: 'tyopaikat',
  sv: 'lediga-jobb',
};
const LOCATION_SEGMENT_BY_LOCALE: Record<SupportedLocale, string> = {
  en: 'location',
  fi: 'alue',
  sv: 'omrade',
};

// Helper functions and computeSearchPageMeta are now imported from ./utils/seoUtils

const activityStripMessages = defineMessages({
  title: {
    id: 'search.activityStrip.title',
    defaultMessage: 'Your job search activity',
    description: 'Heading for the compact activity Sankey strip at the bottom of the search results page',
  },
  cta: {
    id: 'search.activityStrip.cta',
    defaultMessage: 'Manage applications',
    description: 'Button linking to the activity / application management page from the SRP footer strip',
  },
  teaser: {
    id: 'search.activityStrip.teaser',
    defaultMessage: 'Browse, save, and apply to jobs to see your funnel here.',
    description: 'Hint text when the user has no activity data for the SRP Sankey strip',
  },
});

const SearchResultsPage = React.memo(function SearchResultsPage({ 
  initialSort, 
  initialLayout, 
  initialResults, 
  hasSSRData,
  ssrHasNoResults
}: { 
  initialSort?: 'relevance' | 'date' | 'salary', 
  initialLayout?: 'grid' | 'list' | 'map',
  initialResults?: any,
  hasSSRData?: boolean,
  ssrHasNoResults?: boolean
}) {
  const intl = useIntl();
  const { results } = useSearchResults();
  const [helpTrayOpen, setHelpTrayOpen] = useState(false);
  const [feedbackModalOpen, setFeedbackModalOpen] = useState(false);
  const pageContext = usePageContext();
  // Use extracted useViewMode hook
  const { viewMode, setViewMode } = useViewMode({ initialLayout });
  
  // Performance logging
  useRenderLogger('SearchResultsPage', {
    resultsCount: results?.nbHits,
    viewMode,
    hasSSRData,
    initialSort,
    initialLayout,
  });

  // Enable debug logging if ?debug-render is in URL or localStorage
  useEffect(() => {
    if (typeof window !== 'undefined') {
      const urlParams = new URLSearchParams(window.location.search);
      const hasUrlFlag = urlParams.has('debug-render');
      const hasLocalStorageFlag = localStorage.getItem('__DEBUG_RENDER__') === 'true';
      
      if (hasUrlFlag || hasLocalStorageFlag) {
        setDebugRenderEnabled(true);
      }
    }
  }, []);

  // Track all renders and state changes
  useDebugRenderIf('SearchResultsPage', {
    initialSort,
    initialLayout,
    hasSSRData,
    ssrHasNoResults,
    resultsCount: results?.nbHits,
    viewMode,
    helpTrayOpen,
    urlPathname: pageContext?.urlPathname ?? (typeof window !== 'undefined' ? window.location.pathname : '/'),
  });

  // Debug: Log when component renders (only when debug is enabled)
  // Removed - useDebugRenderIf already handles this logging
  // Get locale from react-intl
  const locale = intl.locale as 'fi' | 'en' | 'sv';
  const indexBaseMap = {
    en: 'en_search',
    fi: 'fi_hae',
    sv: 'sv_sok',
  };
  const indexPrefix = indexBaseMap[locale] || 'en_search';

  const { sort, setSort } = useSearchSort();
  const effectiveSort = sort || initialSort || 'relevance';
  const currentRefinement = effectiveSort === 'date'
    ? `${indexPrefix}_postedDate_desc`
    : effectiveSort === 'salary'
      ? `${indexPrefix}_salaryRangeMin_desc`
      : `${indexPrefix}`;
  const refine = (_value: string) => {};

  const handleSortChange = (value: string) => {
    // Determine preference from selected index value
    let sortPreference: 'relevance' | 'date' | 'salary' = 'relevance';
    if (value.includes('postedDate_desc')) sortPreference = 'date';
    else if (value.includes('salaryRangeMin_desc')) sortPreference = 'salary';

    // Update provider state so searches use correct index immediately
    setSort(sortPreference);

    // Update URL (remove sort for relevance to keep URL clean)
    try {
      const current = typeof window !== 'undefined' ? window.location.href : '';
      const u = new URL(current);
      // Clean up any old format parameters
      const oldKeys = Array.from(u.searchParams.keys()).filter(k => k.includes('[sort]'));
      oldKeys.forEach(k => u.searchParams.delete(k));
      
      if (sortPreference === 'relevance') {
        u.searchParams.delete('sort');
      } else {
        u.searchParams.set('sort', sortPreference);
      }
      // Navigate to trigger SSR with new sort
      window.location.assign(u.toString());
    } catch {
      // Fallback: refine locally (no SSR)
      refine(value);
    }
  };

  // Keyboard shortcut: Shift+? (i.e. '?') to open help tray (md and up only)
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (
        e.key === '?' &&
        !helpTrayOpen &&
        window.innerWidth >= 768 &&
        !(document.activeElement instanceof HTMLInputElement) &&
        !(document.activeElement instanceof HTMLTextAreaElement) &&
        !(document.activeElement instanceof HTMLSelectElement)
      ) {
        e.preventDefault();
        setHelpTrayOpen(true);
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [helpTrayOpen]);


  // Get effective results (from context or initial SSR data)
  const effectiveResults = results || initialResults;

  // Check if there are any active search parameters (query or any filters)
  const { query, setQuery } = useSearchQuery();
  const { filters, setFilters } = useSearchFilters();
  const { page, setPage } = useSearchPage();
  
  // Get location from filters and route
  const location = useMemo(() => getLocationFromFiltersAndRoute(filters, pageContext), [filters, pageContext]);

  // Get category ("field" facet) from filters and route — mutually exclusive with location
  // in the header slot (location wins if both are somehow present; see SearchHeader).
  const category = useMemo(() => getCategoryFromFiltersAndRoute(filters, pageContext), [filters, pageContext]);

  // Get location trends data from SSR using useData() (updates on client-side navigation)
  const data = useData<any>();
  const ssrLocationTrendsData = data?.locationTrendsData;
  const categoryHubData = data?.categoryHubData ?? null;
  
  // Use useLocationTrends hook to fetch data client-side when location changes
  // This ensures data is fetched during client-side navigation
  const { data: clientLocationTrendsData } = useLocationTrends(
    location || null,
    locale,
    ssrLocationTrendsData // Pass SSR data as initial data
  );
  
  // Prefer client-fetched data if available, otherwise use SSR data
  const locationTrendsData = clientLocationTrendsData || ssrLocationTrendsData;
  const hasQuery = typeof query === 'string' && query.trim().length > 0;
  let hasFilters = false;
  const parsedFilters = useMemo(() => {
    if (!filters) return {};
    if (typeof filters === 'string') {
      try {
        return JSON.parse(filters) as Record<string, unknown>;
      } catch {
        return {};
      }
    }
    return filters as Record<string, unknown>;
  }, [filters]);

  if (typeof filters === 'string') {
    hasFilters = filters.trim().length > 0;
  } else if (filters && typeof filters === 'object') {
    hasFilters = Object.entries(filters as Record<string, any>).some(([_, v]) => {
      if (Array.isArray(v)) return v.filter(Boolean).length > 0;
      return v !== undefined && v !== null && String(v).length > 0;
    });
  }
  const hasActiveSearch = hasQuery || hasFilters;

  // Check if the search has completed but returned no results
  // Use SSR flag if available, otherwise check client-side
  // IMPORTANT: Never show no-results if there's no active search (no query and no filters)
  const hasNoResults = useMemo(() => {
    // If there's no active search (no query and no filters), always show results (all jobs)
    if (!hasActiveSearch) {
      return false;
    }
    
    // Prefer SSR flag if available (but only if we have an active search)
    if (ssrHasNoResults !== undefined && hasActiveSearch) {
      return ssrHasNoResults;
    }
    
    // Check both results (from context) and effectiveResults (fallback to initialResults)
    // Prefer results from context as it's the most up-to-date
    // With SSR, we always have results on initial render, so no need to check loading state
    const resultsToCheck = results || effectiveResults;
    
    if (resultsToCheck) {
      // Check nbHits first (most reliable)
      if (resultsToCheck.nbHits === 0) {
        return true;
      }
      // Also check hits array as fallback (in case nbHits is not set)
      if (Array.isArray(resultsToCheck.hits) && resultsToCheck.hits.length === 0) {
        return true;
      }
    }
    
    return false;
  }, [ssrHasNoResults, hasSSRData, results, effectiveResults, hasActiveSearch]);

  // Determine if clear button would be visible (same logic as Autocomplete)
  const showClearButton = hasQuery || page > 0 || effectiveSort !== 'relevance';
  const locationFilters =
    Array.isArray((parsedFilters as Record<string, unknown>).location)
      ? ((parsedFilters as Record<string, string[]>).location ?? []).filter(Boolean)
      : [];
  const isSingleLocationPage = locationFilters.length === 1;

  const currentHits = useMemo(() => {
    const activeHits = (results?.hits as DisplayJob[] | undefined) ?? (initialResults?.hits as DisplayJob[] | undefined) ?? [];
    return Array.isArray(activeHits) ? activeHits : [];
  }, [initialResults, results]);

  const pageUrl = useMemo(() => {
    const original = (pageContext as any)?.urlOriginal;
    if (typeof original === 'string' && original.length > 0) {
      return original;
    }
    if (typeof window !== 'undefined') {
      return window.location.href;
    }
    return '';
  }, [pageContext]);

  const { canonicalUrl: searchCanonicalUrl, title: searchTitle, description: searchDescription } = useMemo(() => {
    return computeSearchPageMeta(pageContext, locale as SupportedLocale);
  }, [pageContext, locale]);

  // Get companyId from filters
  const companyId = useMemo(() => {
    if (!filters) return null;
    if (typeof filters === 'object') {
      return (filters as any).companyId?.[0] || null;
    }
    return null;
  }, [filters]);

  return (
    <>
      <KeyboardShortcutsTray
        isOpen={helpTrayOpen}
        onRequestClose={() => setHelpTrayOpen(false)}
        onCloseComplete={() => setHelpTrayOpen(false)}
      />
      {/* <CanonicalTag /> */}
      <div>
        {/* <Configure hitsPerPage={10} /> removed, now handled in SearchWrapper */}
        <div className={`w-full bg-jc-page ${location ? '' : 'pt-1 sm:pt-1 md:pt-2'}`}>
          <div className="max-w-screen-2xl min-w-0 mx-auto sm:px-0 md:px-2 lg:px-8">
          <TopEmployersStripWithClientFetch
            initialEmployers={(location || viewMode === 'map') ? [] : data?.topEmployersStrip}
            locale={locale}
            hideStrip={Boolean(location) || viewMode === 'map'}
          />
          <SearchHeader
            location={location}
            locale={locale}
            nbHits={results?.nbHits || 0}
            locationTrendsData={locationTrendsData}
            category={category}
            categoryHubData={categoryHubData}
          />
          
          <div className="ais-InstantSearch @container w-full min-w-0 pt-1 sm:pt-1 md:pt-2 flex flex-col gap-2">
            <>
              {/* Sort & filter controls + job count – static UI, no skeleton needed */}
              <SearchSidebar
                intl={intl}
                viewMode={viewMode}
                setViewMode={setViewMode}
                currentRefinement={currentRefinement}
                handleSortChange={handleSortChange}
                hasActiveSearch={hasActiveSearch}
                location={location}
                companyId={companyId}
              />
              <SrpAlertCta hasActiveSearch={hasActiveSearch} />
              <SearchResults
                viewMode={viewMode}
                setViewMode={setViewMode}
                hasNoResults={hasNoResults}
                query={query}
              />
            </>
          </div>
          <SearchPagination
            viewMode={viewMode}
            helpTrayOpen={helpTrayOpen}
            setHelpTrayOpen={setHelpTrayOpen}
            feedbackModalOpen={feedbackModalOpen}
            setFeedbackModalOpen={setFeedbackModalOpen}
            pageContextPathname={pageContext?.urlPathname}
          />
          <SrpActivitySankeyStripLive
            viewMode={viewMode}
            locale={locale}
            strings={{
              title: intl.formatMessage(activityStripMessages.title),
              cta: intl.formatMessage(activityStripMessages.cta),
              teaser: intl.formatMessage(activityStripMessages.teaser),
            }}
          />
          </div>
        </div>
      </div>
      <Footer />
      {/* Feedback Modal */}
      <FeedbackModal
        isOpen={feedbackModalOpen}
        onClose={() => setFeedbackModalOpen(false)}
        source="search_results"
        metadata={{
          page: pageContext?.urlPathname,
          viewMode: viewMode,
        }}
      />
    </>
  );
}, (prevProps, nextProps) => {
  // Custom comparison - only re-render if props actually changed
  // This prevents re-renders when parent re-renders but props haven't changed
  return (
    prevProps.initialSort === nextProps.initialSort &&
    prevProps.initialLayout === nextProps.initialLayout &&
    prevProps.initialResults === nextProps.initialResults &&
    prevProps.hasSSRData === nextProps.hasSSRData &&
    prevProps.ssrHasNoResults === nextProps.ssrHasNoResults
  );
});

function Page() {
  const searchContext = useSearchContextOptional();
  const pageContext = usePageContext();
  // Use pageContext.locale so Page can render even when IntlProvider is not yet in ancestry
  // (e.g. static SRP branch or if Layout is missing). SearchResultsPage uses useIntl() and only
  // runs when we have searchContext (inside full layout).
  const locale = (pageContext?.locale as 'fi' | 'en' | 'sv') || 'en';
  const keywords = KEYWORDS[locale] || KEYWORDS.en;
  const data = useData<any>();

  // Memoize props to prevent unnecessary re-renders of SearchResultsPage
  // Compare by actual values, not object references
  const initialSort = (data && (data as any).initialSort) as any;
  const initialLayout = (data as any)?.initialLayout as any;
  const initialResults = (data && (data as any).initialResults) as any;
  const hasSSRData = !!(data && (data as any).initialResults);
  const ssrHasNoResults = (data as any)?.hasNoResults;

  const initialResultsKey = initialResults
    ? `${initialResults.nbHits}-${initialResults.page}-${initialResults.hits?.length || 0}`
    : 'null';
  const homeResultsProps = useMemo(
    () => ({
      initialSort,
      initialLayout,
      initialResults,
      hasSSRData,
      ssrHasNoResults,
    }),
    [
      initialSort,
      initialLayout,
      initialResultsKey,
      hasSSRData,
      ssrHasNoResults,
    ]
  );

  // SRP: do not render until context/results are ready – static shell stays visible until swap.
  if (searchContext === undefined) {
    return null;
  }

  return (
    <PerformanceProfiler id="SearchResultsPage">
      <SearchResultsPage {...homeResultsProps} />
    </PerformanceProfiler>
  );
}

// Expose for non-route consumers without using Vike side exports.
(Page as unknown as { SearchResultsPage?: typeof SearchResultsPage }).SearchResultsPage = SearchResultsPage;

export default Page;
